/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { IconDotsVertical } from "@tabler/icons-react";
import ListView from "../../../components/common/ListView";
import ToggleButton from "../../../components/ToggleButton";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import formatDate from "../../../utilities/helper";
import MessageModal from "../../../components/MessageModal";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";
import ViewCitiesModal from "./ViewCitiesModal";
import AddTireModal from "./AddTireModal";
import {
  useFetchPriceMatrixTireData,
  usePriceMatrixTireCreateMutation,
  useUpdatePriceMatrixTireData,
  useUpdatePriceMatrixTireStatus,
} from "../../../apis/priceMatrixApis";
import { useLanguage } from "../../../context/LanguageContext";

type CoverageFormValues = {
  name: string;
  countryId: string | null;
  warehouseId: string;
  coverageCity: string | null;
};
type Props = {
  isOpen: boolean;
  onClose: () => void;
  onToggle: () => void;
};

export default function TireListTab({ isOpen, onClose, onToggle }: Props) {
  const [searchFor, setSearchFor] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tireItem, setTireItem] = useState<any>(null);
  const [editable, setEditable] = useState(false);
  const [coverageCityError, setCoverageCityError] = useState<string>("");
  const { t } = useLanguage();

  const { mutate, isPending: createPending } =
    usePriceMatrixTireCreateMutation();
  const { mutate: updateTire, isPending: updatePending } =
    useUpdatePriceMatrixTireData();
  const {
    data: tireData,
    refetch: tireRefetch,
    isLoading,
    isFetching,
  } = useFetchPriceMatrixTireData(pageNo, limit, searchFor);
  const { mutate: updateTireStatus, isPending: statusPending } =
    useUpdatePriceMatrixTireStatus();

  const [tireDataArray, setTireDataArray] = useState<any>(tireData);
  const [actionModal, setActionModal] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [citiesArray, setCitiesArray] = useState<any>([]);
  const [isViewCitiesOpen, setIsViewCitiesOpen] = useState(false);

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    watch,
    formState: { errors },
    setValue,
    control,
  } = useForm<CoverageFormValues>();

  useEffect(() => {
    if (tireData) setTireDataArray(tireData);
  }, [tireData]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const resetModalState = () => {
    reset();
    setTireItem(null);
    setEditable(false);
    setCoverageCityError("");
    setIsModalOpen(false);
    onClose();
  };

  const handleAddTire = (data: any) => {
    if (!data?.coverageCity || data?.coverageCity?.length === 0) {
      setCoverageCityError(t("Please select at least one city"));
      return;
    }
    setCoverageCityError("");
    mutate(data, {
      onSuccess: () => {
        toast.success(t("Tier created successfully"));
        resetModalState();
        tireRefetch();
      },
      onError: (error) => toast.error(`${error}`),
    });
  };

  const handleUpdateTire = (data: any) => {
    if (!tireItem) return;
    if (!data?.coverageCity || data?.coverageCity?.length === 0) {
      setCoverageCityError(t("Please select at least one city"));
      return;
    }
    setCoverageCityError("");
    updateTire(
      { id: tireItem.id, ...data },
      {
        onSuccess: () => {
          toast.success(t("Tier updated successfully"));
          resetModalState();
          tireRefetch();
        },
        onError: (error) => toast.error(`${error}`),
      }
    );
  };

  const handleEditData = (item: any) => {
    setEditable(true);
    setTireItem(item);
    reset({
      name: item.name,
      warehouseId: item.warehouseId,
      countryId: item.countryId,
      coverageCity: item.coverageCityIds || [],
    });
    onToggle();
    setIsModalOpen(true);
  };

  const handleToggleStatus = (tire: any) => {
    setTireItem(tire);
    setStatus(tire.status);
    setActionModal(true);
  };

  const handleUpdateStatus = async () => {
    const newStatus = tireItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const updated = tireDataArray.priceMatrixTires.map((v: any) =>
      v.id === tireItem?.id ? { ...v, status: newStatus } : v
    );
    setTireDataArray({ ...tireDataArray, priceMatrixTires: updated });

    await updateTireStatus({ id: tireItem?.id, status: newStatus });
    setActionModal(false);
    setTireItem(null);
  };

  const handleViewCities = (cities: any) => {
    setCitiesArray(cities);
    setIsViewCitiesOpen(true);
  };

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    tireRefetch();
  }, [debouncedSearchFor, pageNo]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  return (
    <div>
      <div className="space-y-4">
        {isOpen && (
          <div className=" p-4 border rounded-2xl bg-[#FCFDF8] space-y-4">
            <div className="flex items-center justify-between">
              <h1 className="text-[#212121]/70 text-base font-medium">
                {editable ? t("Edit Tier") : t("Add Tier")}
              </h1>
            </div>

            <AddTireModal
              onClose={resetModalState}
              register={register}
              errors={errors}
              setValue={setValue}
              watch={watch}
              clearErrors={clearErrors}
              control={control}
              handleSubmit={() =>
                editable
                  ? handleSubmit(handleUpdateTire)()
                  : handleSubmit(handleAddTire)()
              }
              coverageCityError={coverageCityError}
              setCoverageCityError={setCoverageCityError}
              isPending={editable ? updatePending : createPending}
              editable={editable}
              tireItem={tireItem}
              setTireItem={setTireItem}
            />
          </div>
        )}

        {isLoading || isFetching ? (
          <TableSkeleton
            mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
            subClassName=""
            columns={[
              "#",
              t("Tier Name"),
              t("Warehouse"),
              t("Country"),
              t("Date"),
              t("Status"),
              t("Actions"),
            ]}
            rows={6}
          />
        ) : tireDataArray?.priceMatrixTires?.length ? (
          <ListView
            tdClassName="rounded-r-none rounded-l-none"
            trClassName="hover:bg-[#FBD037]/20"
            mainDivClassName="rounded-b-[20px] bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
            mailDivClassName="p-0 bg-transparent rounded-r-none rounded-br-3xl rounded-b-[20px]"
            thClassName="border-t"
            data={tireDataArray?.priceMatrixTires ?? []}
            columns={[
              {
                label: "#",
                render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
              },
              { label: t("Tier Name"), render: (c: any) => c.name },
              { label: t("Country"), render: (c: any) => c.countryName },
              { label: t("Date"), render: (c: any) => formatDate(c.createdAt) },
              {
                label: t("Status"),
                render: (c: any) => (
                  <div className="flex justify-center">
                    <ToggleButton
                      onChange={() => handleToggleStatus(c)}
                      checked={c.status === "ACTIVE"}
                    />
                  </div>
                ),
              },
              {
                label: t("Actions"),
                render: (c: any, i: number) => (
                  <div className="relative flex justify-center">
                    <button
                      onClick={() =>
                        setOpenDropdownIndex(openDropdownIndex === i ? null : i)
                      }
                      className="p-2 border rounded-full bg-[#F7F6F2]"
                    >
                      <IconDotsVertical size={20} />
                    </button>

                    {openDropdownIndex === i && (
                      <div
                        ref={dropdownRef}
                        className="absolute bg-[#F7F6F2] divide-y divide-[#979797]/50 right-20 w-28 -top-8 z-50 mt-2 rounded-xl shadow-lg overflow-hidden"
                      >
                        <div
                          onClick={() => handleViewCities(c.coverageCityNames)}
                          className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                        >
                          {t("View Cities")}
                        </div>
                        <div
                          onClick={() => {
                            handleEditData(c);
                            setOpenDropdownIndex(null); // Close dropdown after click
                          }}
                          className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                        >
                          {t("Edit")}
                        </div>
                      </div>
                    )}
                  </div>
                ),
              },
            ]}
          />
        ) : (
          <NoDataFound
            subClassName=""
            mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
          />
        )}

        {tireDataArray?.totalPages > 1 && (
          <div className="flex justify-center items-center py-4">
            <Pagination
              currentPage={pageNo}
              totalPages={tireDataArray?.totalPages}
              fetchData={handlePaginationFetch}
            />
          </div>
        )}
      </div>
      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title={t("Confirmation Required")}
        subtitle={t("Are you sure you want to perform this action?")}
        img={status === "ACTIVE" ? "/img/inactive.svg" : "/img/active.svg"}
        showFooter={true}
        closeButtonText={t("Cancel")}
        actionButtonText={t("Confirm")}
        actionButtonOnClick={handleUpdateStatus}
        isPending={statusPending}
        size="md"
      />

      <ViewCitiesModal
        citiesItem={citiesArray ?? []}
        open={isViewCitiesOpen}
        onClose={() => setIsViewCitiesOpen(false)}
      />
    </div>
  );
}
